import { useForm } from '@tanstack/react-form'
import { capitalize } from 'lodash'
import { Plus, TriangleAlert } from 'lucide-react'
import { useEffect, useState } from 'react'
import { donationMethods } from '../constants/donationMethods'
import { useAddDonationMutation, useUpdateDonationMutation } from '../hooks'
import {
  DonationCreateSchema,
  DonationUpdateSchema,
} from '../schema/donationSchema'
import { useDonationsStore } from '../store/donationsStore'
import type { ApiError } from '@/types/api'
import type {
  DonationCreateData,
  DonationUpdateData,
} from '../schema/donationSchema'
import type { DonationMethod } from '../types/donation'
import { AutoCompleteSelect } from '@/components/custom/auto-complete-select'
import CurrencyInput from '@/components/custom/currency-input'
import DatePicker from '@/components/custom/date-picker'
import { Modal } from '@/components/custom/modal'
import { FormInput } from '@/components/custom/form-input'
import { FormTextarea } from '@/components/custom/form-textarea'
import LoadingButton from '@/components/custom/loading-button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { useChurchesStore } from '@/features/churches/store/churchesStore'
import { MemberForm } from '@/features/members/components/MemberForm'
import { useMembersStore } from '@/features/members/store/membersStore'
import { notifyRequestError } from '@/utils/notifyRequestError'
import { formatDate, transformDateToTimezone } from '@/utils/timeZoneUtils'
import { useUsersStore } from '@/features/users/store/usersStore'
import { usePermissions } from '@/utils/permissions'
import { Checkbox } from '@/components/ui/checkbox'

const getMethodHelper = (method: string) => {
  const methodKey = method as DonationMethod
  const methodMap: Record<DonationMethod, string> = {
    'e-transfer': 'Reference Number',
    cheque: 'Cheque Number',
    deposit: 'Deposit Number',
    cash: '',
  }
  return methodMap[methodKey] || ''
}

const addDefaultValues = {
  churchId: '',
  donorId: '',
  amount: 0,
  date: '',
  donationRef: '',
  notes: '',
  method: '',
  cashConfirmed: false,
  cashCounterName: '',
  deposited: false,
  isRegistrationFee: false,
}

export function DonationForm({
  id,
  type,
  trigger,
  open,
  onClose,
}: {
  id?: string | null
  type: 'add' | 'edit'
  trigger?: React.ReactNode
  open?: boolean
  onClose?: () => void
}) {
  const { mutateAsync: addDonationMutation, isPending: isAdding } =
    useAddDonationMutation()
  const { mutateAsync: updateDonationMutation, isPending: isUpdating } =
    useUpdateDonationMutation()

  const [openMemberDialog, setOpenMemberDialog] = useState(false)

  const donations = useDonationsStore((s) => s.donations)
  const initial = donations.find((d) => d._id === id)
  const churches = useChurchesStore((s) => s.churches)
  const members = useMembersStore((s) => s.members)
  const users = useUsersStore((s) => s.users)

  // Get permissions for church filtering
  const permissions = usePermissions()

  // Filter churches based on user's role and assignments
  const assignedChurchIds = permissions.getUserAssignedChurches()
  const filteredChurches =
    assignedChurchIds.length === 0
      ? churches.filter((church) => church.active) // Super admin sees all churches
      : churches.filter(
          (church) => church.active && assignedChurchIds.includes(church._id),
        )

  const filteredMembers = members.filter((member) => {
    if (!member.active) return false

    const isMemberChurchAccessible = filteredChurches.some(
      (church) => church._id === member.churchId,
    )

    return isMemberChurchAccessible
  })

  const donationSender = users.find(
    (u) => u._id === initial?.sentToMemberBy,
  )?.name

  const form = useForm({
    defaultValues:
      type === 'edit'
        ? {
            _id: initial?._id || '',
            churchId: initial?.churchId || '',
            donorId: initial?.donorId || '',
            amount: initial?.amount || 0,
            date: initial?.date || '',
            donationRef: initial?.donationRef || '',
            notes: initial?.notes || '',
            method: initial?.method || '',
            cashConfirmed: initial?.cashConfirmed ?? false,
            cashCounterName: initial?.cashCounterName || '',
            deposited: initial?.deposited ?? false,
            isRegistrationFee: initial?.isRegistrationFee ?? false,
          }
        : addDefaultValues,
    onSubmit: async ({ value }) => {
      const transformedValue = {
        ...value,
        date: transformDateToTimezone(value.date),
      }

      try {
        if (type === 'edit') {
          const updatePayload: DonationUpdateData = {
            ...(transformedValue as any),
            status: initial?.status ?? 'pending',
          }
          await updateDonationMutation(updatePayload)
        } else {
          await addDonationMutation(transformedValue as DonationCreateData)
        }
        onClose?.()
        form.reset()
      } catch (error) {
        console.log('Error occurred while submitting form:', error)
        notifyRequestError(
          error as ApiError,
          'Error occurred while submitting donation form',
        )
      }
    },
  })

  const handleClose = () => {
    onClose?.()
    form.reset()
  }

  const schema = type === 'edit' ? DonationUpdateSchema : DonationCreateSchema

  const isPending = isAdding || isUpdating
  const buttonLabel = type === 'add' ? 'Add' : 'Update'
  const loadingLabel = type === 'add' ? 'Adding...' : 'Updating...'

  return (
    <Modal
      open={open}
      onClose={handleClose}
      trigger={trigger}
      title={type === 'edit' ? 'Edit Donation' : 'Add Donation'}
      direction="right"
      contentClassName="min-w-[98vw]"
    >
      {type === 'edit' && initial?.sentToMember && (
        <Alert className="border-red-200 bg-red-50 mb-4">
          <AlertDescription className="flex flex-col items-center text-center space-y-1">
            <Label className="text-red-800 font-bold">
              Receipt previously sent to donor
            </Label>
            <p className="text-red-700 text-sm">
              Sent by:{' '}
              <span className="font-medium">{donationSender ?? 'Unknown'}</span>{' '}
              at{' '}
              <span className="font-medium">
                {initial.sentToMemberAt
                  ? formatDate(initial.sentToMemberAt, 'DD-MM-YYYY')
                  : 'Unknown date'}
              </span>
            </p>
            <p className="text-red-700 text-xs">
              <strong>Note:</strong> Modifying key donation details may require
              sending a new receipt to the donor.
            </p>
          </AlertDescription>
        </Alert>
      )}
      {type === 'edit' && (
        <div className="flex justify-end mb-4">
          <Label className="mr-2 font-normal">Receipt #:</Label>
          <Label>{initial?.receiptNumber}</Label>
        </div>
      )}
      <form
        id="donation-form"
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          form.handleSubmit()
        }}
        className="space-y-2"
      >
        {/* Watch donorId changes and auto-update churchId */}
        <form.Subscribe
          selector={(state) => [state.values.donorId]}
          children={([donorId]) => {
            useEffect(() => {
              if (donorId) {
                const selectedMember = members.find((m) => m._id === donorId)
                if (selectedMember?.churchId) {
                  form.setFieldValue('churchId', selectedMember.churchId)
                }
              }
            }, [donorId])
            return null
          }}
        />

        <form.Field
          name="donorId"
          validators={{
            onBlur: ({ value }) => {
              const result = schema.shape.donorId.safeParse(value)
              return result.success ? undefined : result.error.errors[0].message
            },
          }}
          children={(field) => {
            return (
              <div className="gap-0">
                <AutoCompleteSelect
                  field={field}
                  label="Donor"
                  id={field.name}
                  name={field.name}
                  options={filteredMembers.map((member) => ({
                    value: member._id,
                    label: member.name,
                  }))}
                  required
                  addDialog={
                    <MemberForm
                      type="add"
                      onClose={() => setOpenMemberDialog(false)}
                      open={openMemberDialog}
                      trigger={
                        <Button
                          onClick={() => {
                            setOpenMemberDialog(true)
                          }}
                        >
                          <Plus /> Add Member
                        </Button>
                      }
                    />
                  }
                />
              </div>
            )
          }}
        />

        {/* Hidden churchId field for form validation */}
        <form.Field
          name="churchId"
          validators={{
            onBlur: ({ value }) => {
              const result = schema.shape.churchId.safeParse(value)
              return result.success ? undefined : result.error.errors[0].message
            },
          }}
          children={() => null}
        />

        <div className="grid grid-cols-2 gap-4">
          <form.Field
            name="amount"
            validators={{
              onBlur: ({ value }) => {
                const result = schema.shape.amount.safeParse(value)
                return result.success
                  ? undefined
                  : result.error.errors[0].message
              },
            }}
            children={(field) => {
              return (
                <div className="gap-0">
                  <CurrencyInput
                    field={field}
                    label="Amount"
                    id={field.name}
                    name={field.name}
                    className="text-right"
                    required
                  />
                </div>
              )
            }}
          />

          <form.Field
            name="date"
            children={(field) => (
              <div className="gap-0">
                <DatePicker
                  field={field}
                  label="Date"
                  id={field.name}
                  name={field.name}
                  startYear={2000}
                  disableFutureDates
                  required
                />
              </div>
            )}
          />
        </div>

        <form.Subscribe
          selector={(state) => [state.values.method]}
          children={([method]) => (
            <div className="grid grid-cols-2 gap-4">
              <div
                className={
                  !method || method === 'cash' ? 'col-span-2' : 'col-span-1'
                }
              >
                <form.Field
                  name="method"
                  validators={{
                    onBlur: ({ value }) => {
                      const result = schema.shape.method.safeParse(value)
                      return result.success
                        ? undefined
                        : result.error.errors[0].message
                    },
                  }}
                  children={(field) => {
                    return (
                      <div className="gap-0">
                        <AutoCompleteSelect
                          field={field}
                          label="Donation Method"
                          id={field.name}
                          name={field.name}
                          options={Array.from(donationMethods).map((m) => ({
                            value: m,
                            label: capitalize(m),
                          }))}
                          required
                        />
                      </div>
                    )
                  }}
                />
              </div>

              {!method || method === 'cash' ? null : (
                <div className="col-span-1">
                  <form.Field
                    name="donationRef"
                    validators={{
                      onBlur: ({ value }) => {
                        if (!method || method === 'cash') return undefined

                        const result = schema.shape.donationRef.safeParse(value)
                        return result.success
                          ? undefined
                          : result.error.errors[0].message
                      },
                    }}
                    children={(field) => {
                      return (
                        <div className="gap-0">
                          <FormInput
                            field={field}
                            label={getMethodHelper(method)}
                            id={field.name}
                            name={field.name}
                            required
                          />
                        </div>
                      )
                    }}
                  />
                </div>
              )}
            </div>
          )}
        />

        {/* Cash-specific checklist/UI */}
        <form.Subscribe
          selector={(state) => [
            state.values.method,
            state.values.cashConfirmed,
            state.values.churchId,
          ]}
          children={([method, cashConfirmed, churchId]) =>
            method === 'cash' ? (
              <div className="space-y-4">
                {/* Cash counted confirmation */}
                <form.Field
                  name="cashConfirmed"
                  children={(field) => {
                    return (
                      <div className="flex items-start gap-3">
                        <div className="mt-1">
                          <Checkbox
                            id={field.name}
                            checked={!!field.state.value}
                            onCheckedChange={(v) =>
                              field.handleChange(Boolean(v))
                            }
                            className="h-4 w-4"
                          />
                        </div>
                        <div>
                          <Label htmlFor={field.name} className="font-medium">
                            Was the cash counted by two people (treasurer +
                            another)?
                          </Label>
                          <p className="text-sm text-gray-500">
                            Check yes if two people counted the cash beside the
                            donor.
                          </p>
                        </div>
                      </div>
                    )
                  }}
                />

                {/* If confirmed, pick the counter person from church members */}
                {cashConfirmed && (
                  <form.Field
                    name="cashCounterName"
                    validators={{
                      onBlur: ({ value }) => {
                        if (!cashConfirmed) return undefined
                        return value && value.length > 0
                          ? undefined
                          : 'Please select the counter person (must be a church member)'
                      },
                    }}
                    children={(field) => {
                      // filter members for the currently selected churchId
                      const churchMembers = churchId
                        ? filteredMembers.filter(
                            (m) =>
                              m.churchId === churchId &&
                              m._id !== field.form.getFieldValue('donorId'),
                          )
                        : []

                      return (
                        <div>
                          <AutoCompleteSelect
                            field={field}
                            label="Counter person (church member)"
                            id={field.name}
                            name={field.name}
                            options={churchMembers.map((member) => ({
                              value: member._id,
                              label: member.name,
                            }))}
                            required={Boolean(cashConfirmed)}
                            clearable
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Select a registered member from the donation's
                            church.
                          </p>
                        </div>
                      )
                    }}
                  />
                )}

                {/* 7-day deposit warning when not deposited */}
                <Alert className="border-yellow-200 bg-yellow-50">
                  <AlertDescription className="text-sm text-yellow-800 flex text-center items-center">
                    <TriangleAlert className="w-5 h-5 mr-2" /> Cash must be
                    deposited within 7 days after receiving it.
                  </AlertDescription>
                </Alert>
              </div>
            ) : null
          }
        />

        <form.Field
          name="notes"
          validators={{
            onBlur: ({ value }) => {
              const result = schema.shape.notes.safeParse(value)
              return result.success ? undefined : result.error.errors[0].message
            },
          }}
          children={(field) => (
            <div className="gap-0">
              <FormTextarea
                field={field}
                label="Notes"
                id={field.name}
                name={field.name}
              />
            </div>
          )}
        />

        <form.Field
          name="isRegistrationFee"
          children={(field) => (
            <div className="flex items-start gap-3 pt-1">
              <Checkbox
                id={field.name}
                checked={!!field.state.value}
                onCheckedChange={(v) => field.handleChange(Boolean(v))}
              />
              <div>
                <Label htmlFor={field.name} className="font-medium">
                  Registration Fee
                </Label>
                <p className="text-sm text-muted-foreground">
                  Check if this is a seminar or event registration fee. No
                  receipt will be issued or included in the annual summary.
                </p>
              </div>
            </div>
          )}
        />

        <form.Subscribe
          selector={(state) => [state.canSubmit, state.errors]}
          children={([canSubmit]) => (
            <LoadingButton
              type="submit"
              className="w-full mt-4"
              isLoading={isPending}
              disabled={!canSubmit || isPending}
            >
              {isPending ? loadingLabel : buttonLabel}
            </LoadingButton>
          )}
        />
      </form>
    </Modal>
  )
}
